<template>
  <v-app>
    <app-bar v-if="$store.state.appReady && showOfficeChrome" :showDrawer.sync="showDrawer"/>
    <navigation-drawer v-if="$store.state.axios && $store.state.appReady && showOfficeChrome"
                       v-model="showDrawer"/>
    <v-main>
      <agency-view-bar v-if="showAgencyBar"/>
      <router-view v-if="!showAgencyBar || $store.state.viewAsPartner" :key="routeKey"></router-view>
    </v-main>
    <!-- Navigation in progress, at the top edge so it stays visible on long pages -->
    <v-progress-linear :active="$store.state.loading" class="app-nav-loading" color="info" fixed height="3" indeterminate top/>
    <error-dialog v-if="$store.state.appReady"/>
    <unauthorized-dialog v-if="$store.state.appReady && $store.state.axios"/>
    <share-access-dialog v-if="$store.state.axios"/>
    <snackbar-app/>
    <interrupt-snackbar-app v-if="$store.state.appReady && $store.state.axios"/>
    <emergency-logout-dialog v-if="$store.state.appReady && $store.state.axios"/>
    <survey-dialog v-if="$store.state.appReady && $store.state.axios"/>
    <!-- The old "Verfügbarkeiten aktualisieren" dialog is replaced by the Lehrkraft-Bereich (Heute › Verfügbarkeiten prüfen,
         confirmation and snooze stored on the server) -->

    <v-slide-x-reverse-transition>
      <action-button/>
    </v-slide-x-reverse-transition>
    <cookie-banner/>
    <div style="z-index: 999">
      <portal-target multiple name="overlay"/>
    </div>
    <vanilla-footer v-if="!$route.meta || !($route.meta.noFooter || $route.meta.portal)"/>
  </v-app>
</template>

<script lang="ts">
import Vue from 'vue';
import AppBar from '@/components/app/app-bar.app.vue';
import CookieBanner from '@/components/app/cookieBanner.app.vue';
import SurveyDialog from '@/components/app/surveyDialog.vue';
import {ClientHooks} from '@/hooks';
import {
  applyDocumentTheme,
  readThemePreference,
  resolveDark,
  ThemePreference,
  themeState,
  watchSystemTheme,
} from '@/helpers/themePreference.helper';

export default Vue.extend({
  name: 'App',
  components: {
    VanillaFooter: () => import('@/components/common/vanillaFooter.vue'),
    CookieBanner,
    InterruptSnackbarApp: () => import('@/components/app/interrupt-snackbar-app.vue'),
    EmergencyLogoutDialog: () => import('@/components/app/emergency-logout-dialog.vue'),
    NavigationDrawer: () => import('@/components/app/navigation-drawer.app.vue'),
    AppBar,
    ErrorDialog: () => import('@/components/app/error-dialog.app.vue'),
    UnauthorizedDialog: () => import('@/components/app/unauthorized-dialog.app.vue'),
    SnackbarApp: () => import('@/components/app/snackbar.app.vue'),
    ActionButton: () => import('@/components/app/action-button.app.vue'),
    ShareAccessDialog: () => import('@/components/app/share-access-dialog.vue'),
    SurveyDialog,
    AgencyViewBar: () => import('@/components/salesPartner/agencyViewBar.vue'),
  },
  data: () => ({
    showDrawer: false,
    offSystemTheme: undefined as undefined | (() => void),
  }),
  computed: {
    /** Hell / Dunkel / System from the profile menu */
    themePreference(): ThemePreference {
      return themeState.preference;
    },
    /** Employees open the partner area read-only and pick the agency first */
    showAgencyBar(): boolean {
      return !!this.$store.state.appReady && !!this.$route.meta?.partner && !this.$$user.isPartner;
    },
    /** Office app bar and drawer; not on pages without navbar and not in the Lehrkraft-/Bewerbungs-Bereich (own layout) */
    showOfficeChrome(): boolean {
      return !this.$route.meta || !(this.$route.meta.hideNavbar || this.$route.meta.portal);
    },
    routeKey(): string {
      // Lehrkraft-/Bewerbungs-Bereich: the layout stays, its own router-view is keyed by the route
      if (this.$route.meta?.portal) {
        return `portal:${this.$route.meta.portal}`;
      }
      // Profile pages keep their instance when only ?tab= changes (browser back from a full view)
      const path = this.$route.meta?.keepOnQueryChange ? this.$route.path : this.$route.fullPath;
      return this.showAgencyBar ? `${path}@${this.$store.state.viewAsPartner?.id || ''}` : path;
    },
  },
  watch: {
    /** Every user keeps their own choice; before the login the last choice on this device applies */
    '$store.state.loginUserId': {
      immediate: true,
      handler(id: unknown) {
        themeState.preference = readThemePreference(id || undefined);
      },
    },
    themePreference: {
      immediate: true,
      handler() {
        this.applyTheme();
      },
    },
    /** Keeps <html> in sync also when the theme is switched directly (e.g. in debug mode) */
    '$vuetify.theme.dark': {
      immediate: true,
      handler(dark: boolean) {
        applyDocumentTheme(dark);
      },
    },
    showAgencyBar: {
      immediate: true,
      handler(value: boolean) {
        this.$store.commit('setViewAsPartnerActive', value);
      },
    },
    '$vuetify.breakpoint.mdAndUp'(v: boolean) {
      this.$nextTick(() => {
        this.showDrawer = v;
      });
    },
  },
  methods: {
    applyTheme() {
      this.$vuetify.theme.dark = resolveDark(this.themePreference);
    },
  },
  async mounted() {
    this.offSystemTheme = watchSystemTheme(() => {
      if (this.themePreference === 'system') {
        this.applyTheme();
      }
    });
    await ClientHooks.init();
    if (this.$vuetify.breakpoint.mdAndUp) {
      this.showDrawer = true;
    }
  },
  beforeDestroy() {
    this.offSystemTheme?.();
  },
});
</script>

<style src="./global.css"/>
<style src="./components/app/shell/shell.css"/>
<style src="./components/common/modern/modern.css"/>
